import { DesktopSidebar } from './DesktopSidebar';
import { MobileHeader } from './MobileHeader';
import { MobileNavigation } from './MobileNavigation';
import { VersionBadge } from './VersionBadge';
import type { User } from '@supabase/supabase-js';

interface AppLayoutProps {
  /**
   * Current authenticated user
   */
  user: User | null;
  /**
   * Current active route path
   */
  currentPath: string;
  /**
   * Optional breadcrumb navigation items
   */
  breadcrumbs?: Array<{ label: string; href?: string }>;
  /**
   * Page content to render
   */
  children: React.ReactNode;
  /**
   * Navigation handler
   */
  onNavigate: (path: string) => void;
  /**
   * Sign out handler
   */
  onSignOut: () => void;
}

/**
 * AppLayout - Main application layout wrapper
 * 
 * Provides responsive layout with desktop sidebar and mobile header/navigation.
 * Handles user authentication and navigation state.
 * 
 * @example
 * ```tsx
 * <AppLayout
 *   user={currentUser}
 *   currentPath="/dashboard"
 *   onNavigate={(path) => router.push(path)}
 *   onSignOut={handleSignOut}
 * >
 *   <DashboardPage />
 * </AppLayout>
 * ```
 */
export function AppLayout({
  user,
  currentPath,
  children,
  onNavigate,
  onSignOut,
}: AppLayoutProps) {
  return (
    <div className="flex h-screen w-full overflow-hidden">
      {/* Desktop Sidebar */}
      <DesktopSidebar
        user={user}
        currentPath={currentPath}
        onNavigate={onNavigate}
        onSignOut={onSignOut}
      />

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile Header */}
        <MobileHeader
          user={user}
          onSignOut={onSignOut}
        />

        {/* Content Area */}
        <main 
          id="main-content"
          tabIndex={-1}
          key={currentPath} 
          className="flex-1 overflow-y-auto bg-background"
          style={{ 
            overscrollBehaviorY: 'none',
            paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' // Evita sobreposição da TabBar
          }}
        >
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileNavigation
          currentPath={currentPath}
          onNavigate={onNavigate}
        />

        {/* Version Badge */}
        <VersionBadge />
      </div>
    </div>
  );
}
